import { useState, useRef, useEffect, useImperativeHandle, forwardRef, useCallback, type ReactNode, type Ref } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SelectOption {
  value: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SelectHandle {
  close: () => void;
}

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  dropdownClassName?: string;
  disabled?: boolean;
  searchable?: boolean;
  emptyText?: string;
  renderOption?: (option: SelectOption, isSelected: boolean) => ReactNode;
  /** Fired when the dropdown opens — useful for lazily loading more options. */
  onOpen?: () => void;
  'data-testid'?: string;
}

export const Select = forwardRef(function Select(
  {
    value,
    onValueChange,
    options,
    placeholder = 'Select...',
    className,
    triggerClassName,
    dropdownClassName,
    disabled = false,
    searchable = false,
    emptyText = 'No options',
    renderOption,
    onOpen,
    'data-testid': dataTestId,
  }: SelectProps,
  ref: Ref<SelectHandle>
) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState('');
  const [dropAbove, setDropAbove] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const animTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const selectedOption = options.find((o) => o.value === value);

  const filtered = searchable && search
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(search.toLowerCase()) ||
          o.description?.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  const closeDropdown = useCallback(() => {
    setVisible(false);
    setSearch('');
    // Keep mounted for close animation, then unmount
    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    animTimerRef.current = setTimeout(() => setOpen(false), 180);
  }, []);

  // Expose close method via ref
  useImperativeHandle(ref, () => ({ close: closeDropdown }), [closeDropdown]);

  // Calculate position for the portal dropdown
  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = Math.min(280, filtered.length * 44 + (searchable ? 44 : 0) + 16);
    const shouldDropAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    setDropAbove(shouldDropAbove);

    const rightOffset = window.innerWidth - rect.right;
    // Ensure dropdown doesn't overflow past the left edge of the viewport
    const maxWidth = window.innerWidth - rightOffset - 8;

    setDropdownStyle({
      position: 'fixed',
      right: rightOffset,
      minWidth: rect.width,
      maxWidth,
      ...(shouldDropAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, [filtered.length, searchable]);

  const openDropdown = useCallback(() => {
    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    setOpen(true);
    onOpen?.();
    // Let portal mount first, then trigger enter animation
    requestAnimationFrame(() => {
      updatePosition();
      setVisible(true);
    });
    if (searchable) setTimeout(() => searchRef.current?.focus(), 50);
  }, [updatePosition, searchable, onOpen]);

  const toggleDropdown = useCallback(() => {
    if (open && visible) {
      closeDropdown();
    } else {
      openDropdown();
    }
  }, [open, visible, closeDropdown, openDropdown]);

  // Close on scroll (outside dropdown) and reposition on resize
  useEffect(() => {
    if (!open) return;

    // Grace period: ignore scroll events during opening animation to avoid
    // premature close from layout reflows or scroll-into-view side effects
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 150);

    const handleScroll = (e: Event) => {
      if (!armed) return;
      if (dropdownRef.current?.contains(e.target as Node)) return;
      closeDropdown();
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      clearTimeout(armTimer);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, closeDropdown, updatePosition]);

  // Cleanup timer on unmount
  useEffect(() => () => {
    if (animTimerRef.current) clearTimeout(animTimerRef.current);
  }, []);

  return (
    <div ref={containerRef} className={cn('relative', className)} data-testid={dataTestId}>
      <button
        type="button"
        onClick={() => !disabled && toggleDropdown()}
        disabled={disabled}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background transition-colors',
          'hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          (open && visible) && 'ring-2 ring-ring ring-offset-2',
          triggerClassName
        )}
      >
        <span className={cn('truncate text-left min-w-0', !selectedOption && 'text-muted-foreground')}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', (open && visible) && 'rotate-180')} />
      </button>

      {/* Portal: invisible backdrop + dropdown */}
      {open && createPortal(
        <>
          {/* Backdrop – catches all outside clicks */}
          <div
            className="fixed inset-0 z-[9998]"
            data-select-portal
            onMouseDown={(e) => {
              e.preventDefault();
              closeDropdown();
            }}
          />
          {/* Dropdown panel */}
          <div
            ref={dropdownRef}
            data-select-portal
            style={dropdownStyle}
            className={cn(
              'z-[9999] rounded-lg border border-border bg-popover shadow-lg',
              'transition-all duration-150 ease-out',
              dropAbove ? 'origin-bottom' : 'origin-top',
              visible
                ? 'opacity-100 scale-100 translate-y-0'
                : dropAbove
                  ? 'opacity-0 scale-95 translate-y-1'
                  : 'opacity-0 scale-95 -translate-y-1',
              dropdownClassName
            )}
          >
            {searchable && (
              <div className="px-2 py-1.5 border-b border-border">
                <input
                  ref={searchRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
                />
              </div>
            )}
            <div className="max-h-60 overflow-y-auto overflow-x-hidden p-1 scrollbar-thin">
              {filtered.length === 0 ? (
                <div className="py-4 text-center text-xs text-muted-foreground">
                  {emptyText}
                </div>
              ) : (
                filtered.map((option) => {
                  const isSelected = option.value === value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      disabled={option.disabled}
                      onClick={() => {
                        onValueChange(option.value);
                        closeDropdown();
                      }}
                      className={cn(
                        'relative flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors',
                        'hover:bg-accent hover:text-accent-foreground',
                        'disabled:pointer-events-none disabled:opacity-50',
                        isSelected && 'bg-accent/60'
                      )}
                    >
                      {renderOption ? (
                        renderOption(option, isSelected)
                      ) : (
                        <>
                          {option.icon && <span className="shrink-0 text-muted-foreground">{option.icon}</span>}
                          <span className="flex-1 text-left truncate">{option.label}</span>
                          {option.description && (
                            <span className="shrink-0 text-[10px] text-muted-foreground/70">{option.description}</span>
                          )}
                          {isSelected && (
                            <Check className="h-3 w-3 shrink-0 text-primary" />
                          )}
                        </>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
});
